import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, CreditCard, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATES = {
    house: ["13thav's own register", "This shop is 13thav's account — card orders already land here."],
    live: ["Taking cards", "Card orders are charged straight into your own Stripe account."],
    onboarding: ["Stripe needs a bit more", "You started onboarding but Stripe still wants details before it can pay you."],
    off: ["No card register yet", "Until you connect Stripe, your cart says \"this shop takes payment directly\"."],
};

function stateOf(s) {
    if (!s) return "off";
    if (s.house) return "house";
    if (s.charges_enabled) return "live";
    return s.connected ? "onboarding" : "off";
}

/** Your own Stripe, in your own shop. The mall only takes its fee on paid product orders. */
export default function StripeRegister() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState("");
    const [blocked, setBlocked] = useState("");

    const load = useCallback(() => api.get("/store/stripe")
        .then(({ data }) => setData(data)).catch(() => setData(null)), []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const flag = params.get("stripe");
        if (!flag) return;
        toast.success(flag === "done" ? "Back from Stripe — checking your register…" : "Stripe onboarding restarted");
        params.delete("stripe");
        setParams(params, { replace: true });
        load();
    }, [params, setParams, load]);

    const start = async () => {
        setBusy("connect");
        setBlocked("");
        try {
            const { data: res } = await api.post("/store/stripe/connect", { origin_url: window.location.origin });
            window.location.href = res.url;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail);
            if (e.response?.status === 503) setBlocked(msg); else toast.error(msg);
        } finally { setBusy(""); }
    };

    const openDashboard = async () => {
        setBusy("dashboard");
        try {
            const { data: res } = await api.post("/store/stripe/dashboard");
            window.open(res.url, "_blank", "noopener");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    if (!data) return null;
    const s = data.status || {};
    const key = stateOf(s);
    const [title, blurb] = STATES[key];
    const mode = s.mode || data.mode;
    const live = mode === "live";
    const hasAcct = s.account_name || s.account_email || s.account_id;

    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="stripe-register-block">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">Your card register</p>
                {mode && (
                    <span data-testid="stripe-mode"
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${live ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                        {live ? <BadgeCheck size={11} /> : <AlertTriangle size={11} />} {live ? "Live payments" : "Test mode"}
                    </span>
                )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                <span data-testid="stripe-state"
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em] ${key === "live" || key === "house" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : key === "onboarding" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-zinc-300 bg-zinc-50 text-zinc-600"}`}>
                    {key === "live" || key === "house" ? <BadgeCheck size={11} /> : <CreditCard size={11} />} {title}
                </span>
            </div>
            <p className="mt-3 text-sm text-zinc-600">{blurb}</p>

            {hasAcct && (
                <div className="mt-4 rounded-xl border border-zinc-200 bg-white/70 p-4" data-testid="stripe-account-card">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {s.house ? "Charges on this Stripe account" : "Your connected Stripe account"}
                    </p>
                    <dl className="mt-2 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                        {s.account_name && (
                            <div className="flex justify-between gap-3 sm:block">
                                <dt className="text-zinc-500">Account name</dt>
                                <dd className="font-semibold text-zinc-900" data-testid="stripe-account-name">{s.account_name}</dd>
                            </div>
                        )}
                        {s.account_email && (
                            <div className="flex justify-between gap-3 sm:block">
                                <dt className="text-zinc-500">Account email</dt>
                                <dd className="font-medium text-zinc-800" data-testid="stripe-account-email">{s.account_email}</dd>
                            </div>
                        )}
                        {s.account_id && (
                            <div className="flex justify-between gap-3 sm:block">
                                <dt className="text-zinc-500">Account ID</dt>
                                <dd className="font-mono text-[12px] text-zinc-700" data-testid="stripe-account-id">{s.account_id}</dd>
                            </div>
                        )}
                        {(s.account_country || s.account_currency) && (
                            <div className="flex justify-between gap-3 sm:block">
                                <dt className="text-zinc-500">Country · currency</dt>
                                <dd className="font-medium text-zinc-800" data-testid="stripe-account-locale">{[s.account_country, s.account_currency].filter(Boolean).join(" · ")}</dd>
                            </div>
                        )}
                    </dl>
                    <p className="mt-3 text-[12px] leading-snug text-zinc-500">
                        {live
                            ? "This is the live account real card payments settle into — match this name and ID in your Stripe dashboard."
                            : "You're in Stripe test mode — only test cards work. When you go live, this panel shows your live account so you can confirm which Stripe account you're using."}
                    </p>
                </div>
            )}

            <ul className="mt-4 space-y-1.5 text-sm text-zinc-600">
                <li>· Paid product orders are charged into <span className="font-semibold text-zinc-900">your</span> Stripe — 13thav keeps <span className="font-semibold text-zinc-900">{data.fee_pct}%</span>.</li>
                <li>· {(data.free_forever || []).join(" and ")} stay free — no commission, ever.</li>
                <li>· Stripe handles the card, the payout schedule and the receipts. 13thav never holds your money.</li>
            </ul>

            {blocked && (
                <div className="mt-4 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3.5" data-testid="stripe-blocked">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-sm text-zinc-700">{blocked}</p>
                </div>
            )}

            {!s.house && (
                <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={start} disabled={busy === "connect"} data-testid="stripe-connect-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-black transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60">
                        {busy === "connect" ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
                        {s.connected ? "Finish Stripe setup" : "Connect Stripe"}
                    </button>
                    {s.connected && (
                        <button type="button" onClick={openDashboard} disabled={busy === "dashboard"} data-testid="stripe-dashboard-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-3 text-sm font-semibold text-zinc-800 transition-colors hover:border-zinc-500">
                            {busy === "dashboard" ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} My Stripe dashboard
                        </button>
                    )}
                    <button type="button" onClick={load} data-testid="stripe-refresh-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-700 transition-colors hover:border-zinc-500">
                        <RefreshCw size={12} /> check status
                    </button>
                </div>
            )}
        </div>
    );
}
